import { useQuery } from '@tanstack/react-query';
import { axiosRequest } from '@lib/axios';

/**
 * @typedef {import("@tanstack/react-query").UseQueryResult<PearsResult, Error>} PearsQueryResult
 */
/**
 * @typedef {Object} Site
 * @property {number} id
 * @property {string} label
 */
/**
 * @typedef {Object} PearsResult
 * @property {string[]} languageLabels
 * @property {Site[]} sites
 * @property {Map<number, Site>} sitesById
 */
/**
 * React Query wrapper for the Pears query.
 *
 * @returns {PearsQueryResult}
 */
// Module scope so React Query can memoize the result: the Map is rebuilt per fetch, not per render.
const withSiteIndex = (data) => ({
  ...data,
  sitesById: new Map((data?.sites ?? []).map(site => [site.id, site])),
});

const usePearsQuery = () => {
  /**
   * Custom hook wrapping useQuery
   * @returns {PearsQueryResult} The query result object containing data, isLoading, error, etc.
   */
  return useQuery({
    queryKey: ['pears'],
    queryFn: async () => axiosRequest({ method: 'GET', url: '/data/pears' }),
    select: withSiteIndex,
    structuralSharing: false,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export default usePearsQuery;
